import { Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import type { Selected } from "src/types/tags";
import { useBulkActionTargets } from "../forms/components/bulkActionTargetsContext";
import { trackFormAction } from "../forms/components/trackFormAction";
import type { SchoolFormId } from "../forms/types";
import type { UseTagsReturnType } from "./types";
import { useEnqueueBulkFormTag } from "./useEnqueueBulkFormTag";
import { useTags } from "./useTags";

type UseBulkActionTagsProps = {
  enrollmentPeriodId: uuid;
};

export type UseBulkActionTagsReturnType = UseTagsReturnType & {
  confirmationDialog: React.ReactNode;
  loadTargets: () => Promise<SchoolFormId[]>;
};

export function useBulkActionTags({
  enrollmentPeriodId,
}: UseBulkActionTagsProps): UseBulkActionTagsReturnType {
  const { loadTargets, selectionSize } = useBulkActionTargets();
  const enqueueBulkTag = useEnqueueBulkFormTag();
  const queryClient = useQueryClient();

  const tags = useTags({
    enrollmentPeriodId,
    tagGroupId: null,
    isEditing: true,
  });

  const invalidateQueries = React.useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchFormTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchFormSchoolTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useFetchTagsKey],
    });
    await queryClient.invalidateQueries({
      queryKey: [FSQ.useListFormsKey],
    });
  }, [queryClient]);

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    confirmButton: {
      label: "Confirm",
      colorScheme: "blue",
    },
    translate: true,
  });

  const onTagUpdate = React.useCallback(
    async (tagId: string, selected: Selected) => {
      const actionType = selected === "All" ? "ADD_TAG" : "REMOVE_TAG";
      trackFormAction({
        verb: "Clicked",
        actionType,
        bulk: true,
        count: selectionSize,
        async: true,
        extra: { tag_id: tagId },
      });

      const tagName =
        tags.tagsProps.tags.toNullable()?.find((tag) => tag.id === tagId)
          ?.name ?? "this tag";
      const formLabel = selectionSize === 1 ? "form" : "forms";

      const confirmed = await confirm(
        selected === "All"
          ? {
              header: (
                <Text>{`Apply "${tagName}" to ${selectionSize} ${formLabel}?`}</Text>
              ),
              body: (
                <Text>
                  This will add the tag to the selected {formLabel}. Updates may
                  take a moment to appear.
                </Text>
              ),
              confirmButton: {
                label: "Yes, apply tag",
                colorScheme: "blue",
              },
            }
          : {
              header: (
                <Text>{`Remove "${tagName}" from ${selectionSize} ${formLabel}?`}</Text>
              ),
              body: (
                <Text>
                  This will remove the tag from the selected {formLabel}.
                  Updates may take a moment to appear.
                </Text>
              ),
              confirmButton: {
                label: "Yes, remove tag",
                colorScheme: "blue",
              },
            }
      );
      if (!confirmed) {
        trackFormAction({
          verb: "Canceled",
          actionType,
          bulk: true,
          count: selectionSize,
          async: true,
          extra: { tag_id: tagId },
        });
        return;
      }

      trackFormAction({
        verb: "Confirmed",
        actionType,
        bulk: true,
        count: selectionSize,
        async: true,
        extra: { tag_id: tagId },
      });

      let queued = false;
      if (selected === "All") {
        queued = await enqueueBulkTag({ actionType: "ADD_TAG", tagId });
      } else if (selected === "None") {
        queued = await enqueueBulkTag({ actionType: "REMOVE_TAG", tagId });
      }

      if (queued) await invalidateQueries();
    },
    [
      confirm,
      enqueueBulkTag,
      invalidateQueries,
      selectionSize,
      tags.tagsProps.tags,
    ]
  );

  return {
    ...tags,
    tagsProps: {
      ...tags.tagsProps,
      onTagUpdate,
    },
    confirmationDialog,
    loadTargets,
  };
}
